/**
 * Safely reads a JSON-serialized value from localStorage.
 * Returns `fallback` (default null) if the key is missing, the stored value
 * is not valid JSON, or if localStorage is unavailable (e.g. Safari ITP /
 * private browsing).
 *
 * @param {string} key - The localStorage key to read.
 * @param {*} [fallback=null] - Value to return when the read fails.
 * @returns {*} The parsed value, or `fallback`.
 */
export function safeGetItem(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    console.error('[localStorage] Failed to read key "' + key + '"', e);
    return fallback;
  }
}

/**
 * Safely serializes `value` to JSON and writes it to localStorage.
 * Returns false on failure (e.g. storage quota exceeded, SecurityError in
 * private browsing).
 *
 * @param {string} key - The localStorage key to write.
 * @param {*} value - The value to serialize and store.
 * @returns {boolean} true on success, false on failure.
 */
export function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.error('[localStorage] Failed to write key "' + key + '"', e);
    return false;
  }
}
